import type { TakeableChannel } from "redux-saga";
import type { ForkEffect, TakeEffect } from "redux-saga/effects";
import {
  all,
  call,
  cancel,
  debounce,
  delay,
  fork,
  join,
  put as typedPut,
  SagaGenerator,
  select,
  take as typedTake,
  takeEvery as typedTakeEvery,
  takeLatest as typedTakeLatest,
  takeLeading as typedTakeLeading,
} from "typed-redux-saga";

export { call, select, fork, join, delay, cancel, all, debounce };

import type { RootEvent } from "../content-hub/store/event";

// Narrows a union based on matching (extending) { [TagProp]: Tag }
// TY: https://spin.atomicobject.com/2021/11/10/discriminated-unions-typescript-project/
// R: https://github.com/Microsoft/TypeScript/issues/17915
// R: https://stackoverflow.com/questions/46312206/narrowing-a-return-type-from-a-generic-discriminated-union-in-typescript
type Narrow<
  Union extends { [_ in TagProp]: string },
  TagProp extends string,
  Tag extends string
> = Union extends { [_ in TagProp]: Tag }
  ? Union
  : Tag extends Union[TagProp]
  ? Omit<Union, TagProp> & { [_ in TagProp]: Tag }
  : never;

export type NarrowStoreEvent<TType extends RootEvent["type"]> = Narrow<
  RootEvent,
  "type",
  TType
>;

type workerFn<T> = (
  event: T extends RootEvent["type"] ? NarrowStoreEvent<T> : any
) => any;

export const put = (event: RootEvent) => typedPut(event);

export const take = typedTake as <
  T extends RootEvent["type"] | TakeableChannel<any>
>(
  event: T | T[]
) => SagaGenerator<Extract<RootEvent, { type: T }>, TakeEffect>;

export const takeEvery = typedTakeEvery as <
  T extends RootEvent["type"] | TakeableChannel<any>
>(
  event: T | T[],
  worker: workerFn<T>
) => SagaGenerator<never, ForkEffect<never>>;

export const takeLatest = typedTakeLatest as <
  T extends RootEvent["type"] | TakeableChannel<any>
>(
  event: T | T[],
  worker: workerFn<T>
) => SagaGenerator<never, ForkEffect<never>>;

export const takeLeading = typedTakeLeading as <
  T extends RootEvent["type"] | TakeableChannel<any>
>(
  event: T | T[],
  worker: workerFn<T>
) => SagaGenerator<never, ForkEffect<never>>;
